import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Calculator, 
  Users, 
  Calendar, 
  DollarSign, 
  TrendingUp,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

interface PricingExampleModalProps {
  isOpen: boolean;
  onClose: () => void;
  currencySymbol: string;
}

export function PricingExampleModal({ isOpen, onClose, currencySymbol }: PricingExampleModalProps) {
  const examples = [
    {
      title: "Basic Adventure Package",
      scenario: "2 people, 3 days, with equipment rental",
      basePrice: 150,
      breakdown: [
        { label: "Base Price", amount: 150, description: "Starting price for adventure", type: "base" },
        { label: "Equipment Rental", amount: 25, description: "Fixed charge for equipment", type: "option" },
        { label: "Group Size: 2 People", amount: 0, description: "Multiplier for per-person pricing", type: "multiplier" },
        { label: "Duration: 3 Days", amount: 0, description: "Multiplier for per-day pricing", type: "multiplier" },
        { label: "Meals: Premium Package", amount: 90, description: "$15 per person per day (2 × 3 × $15)", type: "choice" }
      ],
      total: 265
    },
    {
      title: "Premium Group Experience",
      scenario: "4 people, 2 days, with guide and premium meals",
      basePrice: 200,
      breakdown: [
        { label: "Base Price", amount: 200, description: "Starting price for adventure", type: "base" },
        { label: "Private Guide", amount: 80, description: "40% of base price", type: "option" },
        { label: "Group Size: 4 People", amount: 0, description: "Multiplier for per-person pricing", type: "multiplier" },
        { label: "Duration: 2 Days", amount: 0, description: "Multiplier for per-day pricing", type: "multiplier" },
        { label: "Accommodation: Luxury Tents", amount: 160, description: "$20 per person per day (4 × 2 × $20)", type: "choice" },
        { label: "Transportation: Premium", amount: 120, description: "$30 per person (4 × $30)", type: "choice" }
      ],
      total: 560
    },
    {
      title: "Solo Adventure",
      scenario: "1 person, 1 day, minimal add-ons",
      basePrice: 100,
      breakdown: [
        { label: "Base Price", amount: 100, description: "Starting price for adventure", type: "base" },
        { label: "Solo Supplement", amount: 25, description: "Fixed charge for solo travelers", type: "option" },
        { label: "Basic Lunch", amount: 15, description: "$15 per person (1 × $15)", type: "choice" }
      ],
      total: 140
    }
  ];

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'base': return 'bg-gray-100 text-gray-700';
      case 'option': return 'bg-green-100 text-green-700';
      case 'choice': return 'bg-blue-100 text-blue-700';
      case 'multiplier': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Calculator className="w-6 h-6 text-primary" />
            Pricing Calculator Examples
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="bg-gradient-to-r from-primary/10 to-green-50 rounded-lg p-4 border border-green-100">
            <h3 className="font-semibold text-gray-900 mb-2">How the Calculator Works</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Real-time Updates</p>
                  <p className="text-gray-600">Prices update instantly as customers make selections</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <TrendingUp className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Smart Multipliers</p>
                  <p className="text-gray-600">Automatically applies per-person and per-day pricing</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Users className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Flexible Options</p>
                  <p className="text-gray-600">Supports flat rates, percentages, and per-unit pricing</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <DollarSign className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Transparent Breakdown</p>
                  <p className="text-gray-600">Shows exactly how the total price is calculated</p>
                </div>
              </div>
            </div>
          </div>

          {examples.map((example, idx) => (
            <div key={idx} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">{example.title}</h4>
                  <p className="text-gray-600 text-sm">{example.scenario}</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-primary flex items-center">
                    <DollarSign className="h-5 w-5 mr-1" />
                    {example.total.toFixed(2)}
                  </div>
                  <p className="text-xs text-gray-500">Total Price</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm border-b border-gray-200 pb-2">
                  <span className="font-medium text-gray-700">Price Breakdown</span>
                  <span className="text-gray-500">{example.breakdown.length} items</span>
                </div>

                {example.breakdown.map((item, itemIdx) => (
                  <div key={itemIdx} className="flex justify-between items-start text-sm">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">{item.label}</span>
                        <Badge className={`text-xs ${getTypeColor(item.type)}`}>
                          {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                    </div>
                    <span className="font-semibold text-gray-900 ml-4">
                      {item.amount > 0 ? `${currencySymbol}${item.amount.toFixed(2)}` : '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="bg-blue-100 rounded-full p-2">
                <ArrowRight className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-blue-900 mb-1">Try It Yourself</h4>
                <p className="text-blue-800 text-sm">
                  Go back to the preview and interact with the form elements to see how the pricing updates in real-time. 
                  The calculator handles all the complex math automatically!
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={onClose} className="bg-primary hover:bg-[#224924] text-white">
              Got It, Thanks!
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} 